import { AspectRatio, Box, Button, Center, Container, Grid, GridItem, Heading, HStack, SimpleGrid, Spacer, Spinner, Stack, Text, useBreakpointValue, useToast } from '@chakra-ui/react'
import { LoginNotice, Navbar, SmallScreenNotice } from '@stocker/ui-components/custom'
import { getServerData } from '@stocker/ui-components/helpers'
import type { GetStaticProps, InferGetStaticPropsType, NextPage } from 'next'
import NextImage from 'next/image'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { useSystemFinderListingQuery } from '@stocker/codegen/pimcore'
import type { SystemsForEntryPageQuery } from '@stocker/codegen/configurator-backend'
import { useActiveBasketQuery, useActiveCustomerQuery, useClearBasketMutation, useContactPersonQuery, useCreateSystemConfigMutation, useRemoveBasketLineMutation, useSendBasketToOrderMutation, useSystemsForEntryPageQuery, useUpdateBasketLineMutation } from '@stocker/codegen/configurator-backend'
import { useIntl } from 'react-intl'
import { useEffect, useRef } from 'react'

type EntrySystem = SystemsForEntryPageQuery['systems'][number]

/** Pimcore serves asset paths relative to its own host on some endpoints and absolute on others. */
const imageSource = (path: string) =>
  path.startsWith('http') ? path : `${process.env.NEXT_PUBLIC_PIMCORE_BASE_URL ?? ''}${path}`

const SystemTile = ({ system, onSelect }: { system: EntrySystem, onSelect: (id: string) => void }) => {
  const { formatMessage } = useIntl()
  const comingSoon = !system.enable
  const select = () => { if (!comingSoon) onSelect(system.id) }

  return (
    <Stack
      spacing={2}
      p={3}
      align="center"
      textAlign="center"
      bg="white"
      opacity={comingSoon ? 0.45 : 1}
      cursor={comingSoon ? 'default' : 'pointer'}
      _hover={comingSoon ? undefined : { bg: 'primaryBackground.50' }}
      role="button"
      tabIndex={comingSoon ? -1 : 0}
      aria-disabled={comingSoon}
      onClick={select}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          select()
        }
      }}
    >
      {system.image
        ? (
          <Box h="280px" w="100%" display="flex" alignItems="center" justifyContent="center">
            <img
              src={imageSource(system.image)}
              alt={system.name ?? ''}
              style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
            />
          </Box>
          )
        : null}
      <Text fontSize="sm" lineHeight="1.2">{system.name ?? system.buttonText}</Text>
      {comingSoon
        ? (
          <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.05em" color="secondaryText.500">
            {formatMessage({ id: 'configurator-system--coming-soon', defaultMessage: 'Coming soon' })}
          </Text>
          )
        : null}
    </Stack>
  )
}

const SituationBox = ({ heading, systems, onSelect }: {
  heading: string
  systems: EntrySystem[]
  onSelect: (id: string) => void
}) => (
  <Stack spacing={4} bg="primaryBackground.50" p={5} align="stretch">
    <Heading as="h2" fontSize="lg" lineHeight={1.2}>{heading}</Heading>
    <SimpleGrid columns={2} spacing={3}>
      {systems.map((system) => (
        <SystemTile key={system.id} system={system} onSelect={onSelect}/>
      ))}
    </SimpleGrid>
  </Stack>
)

/**
 * The boxes are the "Bauvorhaben" answers of the Systemfinder, so their headings and order are
 * edited there in Pimcore and the storefront Systemfinder changes along with them.
 */
export const getStaticProps = (async (context) => {
  const [serverData, systemFinder] = await Promise.all([
    getServerData(),
    useSystemFinderListingQuery.fetcher()(),
  ])
  const situations = (systemFinder.getSystemFinderListing?.edges?.[0]?.node?.project ?? []).flatMap((project) =>
    project?.projectFilter?.id ? [{ id: project.projectFilter.id, heading: project.headline ?? '' }] : [],
  )
  return {
    props: {
      revalidateTime: Date.now(),
      ...serverData,
      situations,
    },
    revalidate: 60,
  }
}) satisfies GetStaticProps

const IndexPage: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({ situations }) => {
  const router = useRouter()
  const toast = useToast()
  const { formatMessage } = useIntl()
  const initializationStartedRef = useRef(false)

  const { data: systems } = useSystemsForEntryPageQuery()

  const { data: activeCustomer, isLoading: activeCustomerIsLoading } = useActiveCustomerQuery()

  const { mutate: createSystemConfigMutation } = useCreateSystemConfigMutation({
    onSuccess: (data) => {
      if (data.createSystemConfig.__typename === 'SystemConfig') {
        return router.push(`/configurator?systemConfigId=${data.createSystemConfig.id}`)
      }
      toast({
        title: 'Fehler',
        description: data.createSystemConfig.message,
        status: 'error',
        duration: 10000,
        isClosable: true,
      })
      router.push('/')
    },
    onError: (error) => {
      let message = 'Unknown Error'
      if (error instanceof Error) {
        message = error.message
      }
      toast({
        title: 'Fehler',
        description: message,
        status: 'error',
        duration: 10000,
        isClosable: true,
      })
      router.push('/')
    },
  })

  const { data: contactPersonData } = useContactPersonQuery({})
  const contactPerson = contactPersonData?.contactPerson

  const activeBasket = useActiveBasketQuery()
  const updateBasketLine = useUpdateBasketLineMutation({
    onSuccess: () => {
      activeBasket.refetch()
    },
  })
  const removeBasketLine = useRemoveBasketLineMutation({
    onSuccess: () => {
      activeBasket.refetch()
    },
  })
  const sendBasketToOrder = useSendBasketToOrderMutation({
    onSuccess: () => {
      router.push(process.env.NEXT_PUBLIC_STOCKER_CART_URL!)
    },
  })
  const clearBasket = useClearBasketMutation({
    onSuccess: () => {
      activeBasket.refetch()
    },
  })

  const cartProps = {
    cartItems: activeBasket.data?.activeBasket?.basketLines.map(basketLine => ({
      id: basketLine.id,
      name: basketLine.systemConfig.name,
      quantity: basketLine.quantity,
      price: basketLine.totalCost,
      configurationId: basketLine.systemConfig.id,
    })) ?? [],
    updateBasketLine,
    removeBasketLine,
    sendBasketToOrder,
    basketId: activeBasket.data?.activeBasket?.id ?? '',
    clearBasket,
  }

  // Auto-initialize configurator when systemId is in URL (from SystemFinder)
  useEffect(() => {
    const systemId = router.query.systemId as string | undefined

    if (systemId && !initializationStartedRef.current && activeCustomer?.activeCustomer?.id) {
      initializationStartedRef.current = true

      // Call mutation to create new system config and redirect to configurator
      createSystemConfigMutation({ systemId })
    }
  }, [router.query.systemId, activeCustomer?.activeCustomer?.id, createSystemConfigMutation])

  const showDeviceTooSmallNotice = useBreakpointValue(
    {
      base: true,
      lg: false,
    },
    {
      fallback: 'lg',
    },
  )

  if (showDeviceTooSmallNotice) {
    return (
      <SmallScreenNotice/>
    )
  }

  const allSystems = systems?.systems ?? []
  const startConfiguration = (systemId: string) => { createSystemConfigMutation({ systemId }) }

  // A Bauvorhaben without a linked configurator (Zubehör) gets no box. Until any system is linked
  // to the Systemfinder, the flat list stays.
  const boxes = situations
    .map((situation) => ({
      ...situation,
      systems: allSystems.filter((system) => system.systemFinderFilterIds?.includes(situation.id)),
    }))
    .filter((box) => box.systems.length > 0)
  const groupedSystems = boxes.length > 0 ? boxes : null

  return (
    <Container maxW="container.page">
      {activeCustomerIsLoading
        ? (
          <Center height="50vh">
            <Spinner size="xl"/>
          </Center>
          )
        : (
          <Box>
            {activeCustomer?.activeCustomer?.id
              ? (
                <Grid
                  templateColumns="repeat(6, 1fr)"
                  columnGap={10}
                  rowGap={5}
                  gridTemplateRows="50px auto"
                >
                  <GridItem colSpan={6}>
                    <Navbar
                      contactPartner={{
                        name: contactPerson?.name ?? '',
                        role: contactPerson?.position ?? '',
                        email: contactPerson?.email ?? '',
                        phone: contactPerson?.phone ?? '',
                        image: {
                          fullpath: contactPerson?.image ?? '',
                        },
                      }}
                      user={{
                        name: `${activeCustomer.activeCustomer.firstName} ${activeCustomer.activeCustomer.lastName}`,
                        shortCode: `${activeCustomer.activeCustomer.firstName.slice(0, 1)}${activeCustomer.activeCustomer.lastName.slice(0, 1)}`,
                        amountOfNotifications: 2,
                        menuItems: [
                          {
                            title: 'Zur Accountverwaltung',
                            href: process.env.NEXT_PUBLIC_STOCKER_ACCOUNT_URL ?? 'https://stocker-kaminsysteme.com/account',
                          },
                          {
                            title: 'Meine Konfigurationen',
                            href: process.env.NEXT_PUBLIC_STOCKER_LIST_CONFIGURATIONS_URL ?? 'https://stocker-kaminsysteme.com/account/configurations',
                            // hasNotification: true,
                          },
                          {
                            title: 'Neue Konfiguration',
                            href: '/',
                            // hasNotification: true,
                          },
                        ],
                      }}
                      cartProps={cartProps}
                    />
                  </GridItem>
                  <GridItem colSpan={6}>
                    <Box pt={8} pb={3}>
                      <NextImage
                        src="/img/logo_header.svg"
                        width={120}
                        height={35}
                        alt="Stocker Logo"
                      />
                      <Heading lineHeight={1} fontSize="4xl">
                        {formatMessage({ id: '--configurator' })}
                      </Heading>
                    </Box>
                    <Stack pt={8} pb={12} spacing={8} display={{ base: 'none', md: 'flex' }}>
                      <HStack align="stretch" spacing={8}>
                        <Box flex="1" bg="primaryBackground.50" px="40px" py="35px">
                          <Box dangerouslySetInnerHTML={{ __html: formatMessage({ id: 'configurator-intro-text' }) }} />
                        </Box>
                        <AspectRatio ratio={16 / 9} w="45%" minH="300px" pos="relative">
                          <NextImage src="/img/Mockup_Konfigurator.jpg" layout="fill" objectFit="cover" alt="Konfigurator Mockup"/>
                        </AspectRatio>
                      </HStack>

                      {groupedSystems
                        ? (
                          <SimpleGrid columns={3} spacing={6} alignItems="stretch">
                            {groupedSystems.map((situation) => (
                              <SituationBox
                                key={situation.id}
                                heading={situation.heading}
                                systems={situation.systems}
                                onSelect={startConfiguration}
                              />
                            ))}
                          </SimpleGrid>
                          )
                        : (
                          <Stack spacing={3} maxW="600px">
                            {allSystems.filter((system) => system.enable).map((system) => (
                              <Button
                                key={system.id}
                                w="100%"
                                colorScheme="accent"
                                iconSpacing={3}
                                onClick={() => { startConfiguration(system.id) }}
                              >
                                {system.buttonText?.toUpperCase()}
                              </Button>
                            ))}
                          </Stack>
                          )}

                      <HStack bg="primaryText.500" color="primaryBackground.500" px={6} py={4} spacing={4}>
                        <Text fontWeight="bold">{formatMessage({ id: 'configurator-system-selector--which-system-is-for-me' })}</Text>
                        <Text fontSize="sm">{formatMessage({ id: 'configurator-system-selector--system-finder-tooltip' })}</Text>
                        <Spacer/>
                        <Button as={NextLink} href="https://stocker-kaminsysteme.com/systemfinder" colorScheme="accent" px={8}>
                          {formatMessage({ id: 'configurator-system-selector--to-system-finder' })}
                        </Button>
                      </HStack>
                    </Stack>
                  </GridItem>
                </Grid>
                )
              : (
                <LoginNotice redirect={process.env.NEXT_PUBLIC_CONFIGURATOR_FRONTEND_URL}/>
              )}
          </Box>
          )}
    </Container>
  )
}

export default IndexPage
